import React, { useEffect, useState } from "react";
import { X, Calendar, User, ChevronDown, ChevronUp, FileText, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchObservationEvaluationsByTeacher } from "../../redux/observationEvaluationSlice";

const ViewAdminObservationEvaluationsModal = ({
  isOpen,
  onClose,
  teacherEmail,
  teacherDisplayName,
  campusClasses,
  onAddNew,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { evaluations, loading } = useSelector((state) => state.observationEvaluation);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (isOpen && teacherEmail) {
      dispatch(fetchObservationEvaluationsByTeacher(teacherEmail));
    }
  }, [isOpen, teacherEmail, dispatch]);

  // Debug: Log evaluations data to verify step 6 and 7 are being fetched
  useEffect(() => {
    if (evaluations && evaluations.length > 0) {
      console.log("Observation Evaluations Data:", evaluations);
      evaluations.forEach((ev, idx) => {
        console.log(`Evaluation ${idx + 1}:`, {
          id: ev.id,
          hasPrincipalsRemarks: !!ev.principalsRemarks,
          principalsRemarks: ev.principalsRemarks,
          hasAttachments: !!ev.attachments,
          attachments: ev.attachments,
        });
      });
    }
  }, [evaluations]);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getClassName = (classId) => {
    if (!classId || !campusClasses) return classId || "N/A";
    const found = campusClasses?.flatMap((c) => c.classes || []).find((cls) => cls.id === classId);
    return found?.className || classId;
  };

  const getSubjectName = (subjectId) => {
    if (!subjectId || !campusClasses) return subjectId || "N/A";
    const found = campusClasses
      ?.flatMap((c) => c.classes || [])
      ?.flatMap((cls) => cls.sections || [])
      ?.flatMap((s) => s.subjects || [])
      ?.find((sub) => sub.id === subjectId);
    return found?.subjectName || subjectId;
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const SectionCard = ({ title, icon: Icon, children, titleClassName = "" }) => (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
        {Icon && <Icon size={18} className={`${titleClassName || "text-blue-500"} shrink-0`} />}
        <h4 className={`font-semibold ${titleClassName || "text-gray-800"}`}>{title}</h4>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  const InfoRow = ({ label, value }) => (
    <div className="flex flex-wrap gap-x-2 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-gray-500 font-medium min-w-[140px]">{label}</span>
      <span className="text-gray-800">{value || "—"}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-gray-50 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 bg-white border-b border-gray-200">
          <h2 className="text-2xl font-bold text-blue-600">
            {t("Observation Evaluations") || "Observation Evaluations"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <div className="px-5 py-4 bg-white border-b border-gray-200 flex flex-wrap justify-between items-center gap-3">
          <p className="text-gray-700">
            <span className="text-gray-500 font-medium">{t("Teacher") || "Teacher"}:</span>{" "}
            <strong className="text-gray-900">{teacherDisplayName || teacherEmail || "N/A"}</strong>
          </p>
          {onAddNew && (
            <button
              type="button"
              onClick={onAddNew}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium shadow-sm transition-colors"
            >
              {t("+ Add New") || "+ Add New"}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent" />
            </div>
          ) : !evaluations?.length ? (
            <div className="text-center py-16 px-4 bg-white rounded-xl border border-gray-200">
              <FileText size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 text-lg">{t("No observation evaluations yet") || "No observation evaluations yet"}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {evaluations.map((ev) => {
                const bi = ev.basicInformation || {};
                const isExpanded = expandedId === ev.id;
                // Ensure we properly extract principalsRemarks and attachments from the API response
                const remarks = ev.principalsRemarks || null;
                const attachmentList = ev.attachments && Array.isArray(ev.attachments) ? ev.attachments : (ev.attachments ? [ev.attachments].flat() : []);

                return (
                  <div key={ev.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleExpand(ev.id)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-600">
                          <Calendar size={24} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{formatDate(bi.observationDate)}</p>
                          <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                            <User size={14} />
                            {bi.observerName || "N/A"}
                            {bi.observerDesignation && (
                              <span className="text-gray-400"> · {bi.observerDesignation}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={22} className="text-gray-400 shrink-0" />
                      ) : (
                        <ChevronDown size={22} className="text-gray-400 shrink-0" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50/50 p-5 space-y-6">
                        <SectionCard title={t("Basic Information") || "Basic Information"} icon={FileText}>
                          <div className="space-y-0">
                            <InfoRow label={t("Class") || "Class"} value={getClassName(bi.classGrade)} />
                            <InfoRow label={t("Subject") || "Subject"} value={getSubjectName(bi.subject)} />
                            <InfoRow label={t("Lesson Topic") || "Lesson Topic"} value={bi.lessonTopic} />
                            <InfoRow label={t("Observer Designation") || "Observer Designation"} value={bi.observerDesignation} />
                          </div>
                        </SectionCard>

                        {[ev.domain1, ev.domain2, ev.domain3, ev.domain4].filter(Boolean).map((domain, idx) => {
                          // Apply specific colors to each domain title
                          const domainColors = [
                            "text-[#217223]", // Domain 1: Dark green
                            "text-[#f7b500]", // Domain 2: Orange-yellow
                            "text-[#5B2C84]", // Domain 3: Deep purple
                            "text-[#ff6b35]", // Domain 4: Orange
                          ];
                          const domainColor = domainColors[idx] || undefined;
                          return (
                          <SectionCard
                            key={idx}
                            title={domain.domainTitle || `Domain ${idx + 1}`}
                            icon={MessageSquare}
                            titleClassName={domainColor}
                          >
                            <ul className="space-y-4">
                              {(domain.responses || []).map((r, i) => (
                                <li key={i} className="pl-4 border-l-2 border-blue-200">
                                  <p className="font-medium text-gray-800 mb-1">{r.questionKey}</p>
                                  <p className="text-sm">
                                    <span className="text-gray-500 font-medium">{t("Rating") || "Rating"}:</span>{" "}
                                    <span className="text-gray-800">{r.selectedRating}</span>
                                  </p>
                                  {r.comment && (
                                    <p className="text-sm mt-1 text-gray-600">
                                      <span className="text-gray-500 font-medium">{t("Comment") || "Comment"}:</span> {r.comment}
                                    </p>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </SectionCard>
                          );
                        })}

                        {/* Principal's Remarks - Always show this section */}
                        <SectionCard title={t("Principal's Remarks") || "Principal's Remarks"} icon={MessageSquare}>
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-1">{t("Strengths Observed") || "Strengths Observed"}</p>
                              <p className="text-gray-800 whitespace-pre-wrap">
                                {remarks?.strengthsObserved || "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-1">{t("Areas For Improvement") || "Areas for Improvement"}</p>
                              <p className="text-gray-800 whitespace-pre-wrap">
                                {remarks?.areasForImprovement || "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-1">{t("Action Plan") || "Action Plan"}</p>
                              <p className="text-gray-800 whitespace-pre-wrap">
                                {remarks?.actionPlan || "—"}
                              </p>
                            </div>
                            {remarks?.followUpDate && (
                              <InfoRow label={t("Follow Up Date") || "Follow-up Date"} value={formatDate(remarks.followUpDate)} />
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-1">{t("Overall Rating") || "Overall Rating"}</p>
                              <p className="text-gray-800 font-medium">
                                {remarks?.overallRating || "—"}
                              </p>
                              {remarks?.overallRatingDetails && (
                                <p className="text-gray-600 text-sm mt-1">{remarks.overallRatingDetails}</p>
                              )}
                            </div>
                          </div>
                        </SectionCard>

                        {/* Attachments / Evidence - Always show this section */}
                        <SectionCard title={t("Attachments / Evidence") || "Attachments / Evidence"} icon={FileText}>
                          {attachmentList.length > 0 ? (
                            <ul className="space-y-2">
                              {attachmentList.map((url, i) => (
                                <li key={i}>
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline flex items-center gap-2 text-sm"
                                  >
                                    <FileText size={14} />
                                    {t("Attachment") || "Attachment"} {i + 1}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-500 text-sm italic">{t("No attachments uploaded") || "No attachments uploaded"}</p>
                          )}
                        </SectionCard>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewAdminObservationEvaluationsModal;
